import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Download,
  Check,
  Mail,
  MapPin,
  Phone,
  ArrowUpRight,
  Layers,
  RefreshCw,
  LifeBuoy,
  Code2,
  Network,
  Rocket,
  TrendingUp,
  ClipboardCheck,
  GraduationCap,
} from "lucide-react";
import { downloadBrochureDoc } from "@/lib/brochureDoc";

/* ---------- data ---------- */
const STATS = [
  { k: "30+", v: "SAP SMEs" },
  { k: "10+", v: "Industry Expertise" },
  { k: "20+", v: "Solution Templates" },
  { k: "25+", v: "Years of Experience" },
];

const INDUSTRIES = [
  "Public Sector",
  "Banking & Finance",
  "Manufacturing",
  "Telecom",
  "Travel",
  "Retail",
  "Healthcare",
  "Dairy",
  "Textile",
  "Energy & Utilities",
];

const SERVICES = [
  {
    icon: Layers,
    title: "Implementations",
    body: "Our proven SAP methodology delivers projects on time, within budget, and to your exact specifications through a structured, phased approach for business success.",
  },
  {
    icon: RefreshCw,
    title: "Migrations",
    body: "Our proven SAP methodology delivers projects on time, within budget, and to your exact specifications through a structured, phased approach for business success.",
  },
  {
    icon: LifeBuoy,
    title: "Support",
    body: "Our SAP support team offers expert assistance across all modules, ensuring peak performance, smooth operation and continuous system improvement.",
  },
  {
    icon: Code2,
    title: "Application Development",
    body: "Our tailored SAP development and integration ensure your solutions align with current operations, support future growth, and harmonize with business requirements.",
  },
  {
    icon: Network,
    title: "Integration",
    body: "Our proven SAP methodology delivers projects on time, within budget, and to your exact specifications through a structured, phased approach for business success.",
  },
  {
    icon: Rocket,
    title: "Roll Out",
    body: "Our SAP rollout services help organizations standardize processes, achieve faster deployments, and ensure alignment with global business objectives.",
  },
  {
    icon: TrendingUp,
    title: "Upgrades",
    body: "Our SAP upgrade services are designed to deliver a seamless transition to newer SAP versions — technical, functional, or a full migration to SAP S/4HANA.",
  },
  {
    icon: ClipboardCheck,
    title: "Testing",
    body: "Our SAP Testing Services are designed to provide comprehensive, end-to-end testing that ensures the performance, reliability, and scalability of your SAP applications.",
  },
  {
    icon: GraduationCap,
    title: "Training",
    body: "By providing tailored learning experiences, we ensure your teams can efficiently leverage SAP's capabilities to meet your business objectives.",
  },
];

const SOLUTIONS = [
  "SAP S/4HANA Private Cloud",
  "SAP S/4HANA Public Cloud",
  "SAP Ariba",
  "SAP SuccessFactors",
  "SAP Commerce Cloud",
  "SAP Concur",
  "SAP Manufacturing",
  "SAP Fieldglass",
  "SAP Digital Manufacturing",
  "SAP Business Networks",
  "SAP HXM",
  "SAP Mobility",
  "SAP Field Service Mgmt.",
  "SAP Product Lifecycle Mgmt.",
  "SAP EWM",
];

const SECTIONS = [
  { id: "cover", label: "Cover" },
  { id: "about", label: "About" },
  { id: "mission", label: "Mission & Vision" },
  { id: "stats", label: "Numbers" },
  { id: "industries", label: "Industries" },
  { id: "services", label: "Services" },
  { id: "solutions", label: "Solutions" },
  { id: "contact", label: "Contact" },
];

/* ---------- motion ---------- */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: i * 0.06 },
  }),
};

const maskLine = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.19, 1, 0.22, 1], delay: 0.1 + i * 0.12 },
  }),
};

/* ---------- helpers ---------- */
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: "smooth" });
};

/* ---------- component ---------- */
export default function Brochure() {
  const [downloaded, setDownloaded] = useState(false);
  const [activeSection, setActiveSection] = useState("cover");
  const coverRef = useRef(null);

  const { scrollYProgress: coverProgress } = useScroll({
    target: coverRef,
    offset: ["start start", "end start"],
  });
  const coverY = useTransform(coverProgress, [0, 1], [0, 120]);
  const { scrollYProgress: pageProgress } = useScroll();
  const railScale = useTransform(pageProgress, [0, 1], [0, 1]);

  // Scroll-spy for sidebar active state
  useEffect(() => {
    const observers = [];
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-40% 0px -55% 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const handleDownload = () => {
    downloadBrochureDoc();
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2600);
  };

  return (
    <div data-testid="brochure-page" className="bg-white text-[#0F172A]">
      {/* Global scroll progress rail */}
      <motion.div
        style={{ scaleX: railScale, transformOrigin: "0% 50%" }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#C9A24E] z-[60]"
      />

      {/* Sticky download + section-nav dock */}
      <div className="fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 z-40 hidden md:block">
        <ul className="space-y-3">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => scrollToId(s.id)}
                data-testid={`brochure-nav-${s.id}`}
                aria-label={`Jump to ${s.label}`}
                className="group flex items-center gap-3"
              >
                <span
                  className={`h-px transition-all duration-500 ${
                    activeSection === s.id
                      ? "w-10 bg-[#C9A24E]"
                      : "w-4 bg-[#0F172A]/25 group-hover:w-8 group-hover:bg-[#0F172A]/60"
                  }`}
                />
                <span
                  className={`text-[10px] uppercase tracking-[0.28em] transition-colors ${
                    activeSection === s.id
                      ? "text-[#0F172A]"
                      : "text-[#0F172A]/40 group-hover:text-[#0F172A]/80"
                  }`}
                >
                  {s.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Floating download CTA (bottom-left) */}
      <div className="fixed left-4 sm:left-6 bottom-6 z-40">
        <button
          type="button"
          onClick={handleDownload}
          data-testid="brochure-download-fab"
          className="group relative inline-flex items-center gap-3 bg-[#0B1220] text-white px-5 py-3 sm:px-6 sm:py-3.5 shadow-lg shadow-black/20 hover:bg-[#111827] transition-all"
        >
          <AnimatePresence mode="wait">
            {downloaded ? (
              <motion.span
                key="ok"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                className="inline-flex items-center gap-2 text-sm font-medium"
              >
                <Check size={16} className="text-[#C9A24E]" />
                Downloaded
              </motion.span>
            ) : (
              <motion.span
                key="dl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="inline-flex items-center gap-2 text-sm font-medium"
              >
                <Download size={16} className="text-[#C9A24E]" />
                Save as Word
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* ============ COVER ============ */}
      <section
        id="cover"
        ref={coverRef}
        className="relative overflow-hidden bg-[#0B1220] text-white min-h-[92vh] flex items-center"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#C9A24E]/10 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/25 blur-[140px]" />
        </div>
        <div className="absolute inset-0 grain opacity-40" />

        <motion.div
          style={{ y: coverY }}
          className="relative max-w-7xl mx-auto px-6 lg:px-8 py-28 sm:py-36 w-full"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#C9A24E]"
              >
                <span className="w-8 h-px bg-[#C9A24E]" />
                Digital Company Brochure · V1.1
              </motion.div>

              <h1 className="font-serif mt-8 text-[54px] sm:text-[92px] lg:text-[116px] leading-[0.92] tracking-[-0.02em]">
                {["Intelligent", "SAP Solutions."].map((word, i) => (
                  <span key={word} className="block overflow-hidden">
                    <motion.span
                      custom={i}
                      variants={maskLine}
                      initial="hidden"
                      animate="show"
                      className="block"
                    >
                      {i === 1 ? (
                        <span className="italic text-[#C9A24E]">{word}</span>
                      ) : (
                        word
                      )}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <div className="mt-10 max-w-xl overflow-hidden">
                <motion.p
                  variants={maskLine}
                  custom={2}
                  initial="hidden"
                  animate="show"
                  className="text-lg sm:text-xl text-white/70 leading-relaxed"
                >
                  Scalable, Secure, and Smart — SAP Solutions for Success.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="mt-14 flex items-center gap-8 text-[11px] uppercase tracking-[0.28em] text-white/50"
              >
                <span>Sunsri Software Pvt. Ltd.</span>
                <span className="w-8 h-px bg-white/25" />
                <a
                  href="https://www.sunsris.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#C9A24E] hover:text-white transition"
                >
                  www.sunsris.com
                </a>
              </motion.div>
            </div>

            <div className="md:col-span-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 1 }}
                className="border border-white/20 backdrop-blur-md bg-white/[0.04] p-6"
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#C9A24E]">
                  Save Offline
                </div>
                <p className="font-serif text-xl mt-3 leading-snug">
                  Read the full brochure on the page, or download as a Word document.
                </p>
                <button
                  type="button"
                  onClick={handleDownload}
                  data-testid="brochure-download-hero"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white border-b border-[#C9A24E] pb-1 hover:text-[#C9A24E] transition-colors"
                >
                  <Download size={14} /> Download .doc
                </button>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="absolute bottom-8 left-6 lg:left-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-white/60"
          >
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              className="w-px h-10 bg-white/40 block"
            />
            Scroll to read
          </motion.div>
        </motion.div>
      </section>

      {/* ============ ABOUT ============ */}
      <section
        id="about"
        className="relative py-28 sm:py-36 bg-white"
        data-testid="brochure-about"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="sticky top-32"
              >
                <div className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase">
                  01 — About
                </div>
                <h2 className="font-serif text-3xl sm:text-5xl mt-6 leading-[1.05] text-[#0B1220]">
                  Your partner for <span className="italic text-[#0F172A]/60">software innovation.</span>
                </h2>
              </motion.div>
            </div>
            <div className="md:col-span-8 space-y-6">
              {[
                "We specialize in delivering comprehensive SAP solutions tailored to drive business transformation and innovation. With years of expertise in SAP implementation, support, and enhancement, we empower organizations to optimize their processes, improve efficiency, and achieve measurable results.",
                "Our team of certified SAP professionals brings a blend of technical expertise, industry knowledge, and a passion for problem-solving. From small enterprises to large global corporations, we partner with businesses across diverse industries to design and deploy customized SAP solutions that align with their unique objectives.",
              ].map((p, i) => (
                <motion.p
                  key={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fadeUp}
                  custom={i + 1}
                  className="text-lg text-gray-700 leading-[1.75]"
                >
                  {p}
                </motion.p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ MISSION & VISION ============ */}
      <section
        id="mission"
        className="relative py-28 sm:py-36 bg-[#F5F5F0] border-y border-gray-200"
        data-testid="brochure-mission"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase mb-6"
          >
            02 — Purpose
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[#0B1220]/15">
            {/* Mission */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              className="p-8 sm:p-12 border-b md:border-b-0 md:border-r border-[#0B1220]/15"
            >
              <div className="overline text-[#C9A24E]">Mission</div>
              <h3 className="font-serif text-3xl sm:text-4xl mt-6 leading-tight text-[#0B1220]">
                Value-driven SAP <br />
                <span className="italic">for real outcomes.</span>
              </h3>
              <p className="text-gray-700 mt-6 leading-relaxed">
                We are committed to delivering value-driven SAP solutions that help businesses:
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Simplify complex operations",
                  "Drive sustainable growth",
                  "Adapt seamlessly to evolving market dynamics",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-[15px] text-gray-800">
                    <span className="mt-2 w-6 h-px bg-[#C9A24E] shrink-0" />
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={1}
              className="p-8 sm:p-12 relative overflow-hidden"
            >
              <div className="overline text-[#C9A24E]">Vision</div>
              <h3 className="font-serif text-3xl sm:text-4xl mt-6 leading-tight text-[#0B1220]">
                A trusted leader in <br />
                <span className="italic">SAP services.</span>
              </h3>
              <p className="text-gray-700 mt-6 leading-relaxed max-w-md">
                To be a trusted leader in SAP services, enabling businesses to unlock their full
                potential through innovative technology and strategic insights.
              </p>
              <div className="mt-10 font-serif text-[110px] leading-none text-[#0B1220]/10 select-none">
                ✦
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section
        id="stats"
        className="relative py-28 sm:py-36 bg-[#0B1220] text-white overflow-hidden"
        data-testid="brochure-stats"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-0 w-[500px] h-[500px] rounded-full bg-[#C9A24E]/10 blur-[140px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase"
          >
            03 — By the Numbers
          </motion.div>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="font-serif text-4xl sm:text-6xl mt-6 leading-[1.05] tracking-tight max-w-3xl"
          >
            Two and a half decades of <span className="italic text-[#C9A24E]">measurable SAP delivery.</span>
          </motion.h2>

          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-white/15">
            {STATS.map((s, i) => (
              <motion.div
                key={s.v}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className={`p-8 border-b border-white/15 ${
                  i < 3 ? "lg:border-r border-white/15" : ""
                } ${i % 2 === 0 ? "border-r border-white/15" : ""} lg:border-r-white/15`}
              >
                <div className="font-serif text-6xl sm:text-7xl leading-none tracking-tight">
                  {s.k}
                </div>
                <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-white/50">
                  {s.v}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INDUSTRIES ============ */}
      <section
        id="industries"
        className="relative py-28 sm:py-36 bg-white"
        data-testid="brochure-industries"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-6">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase"
              >
                04 — Industries
              </motion.div>
              <motion.h2
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="font-serif text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-[#0B1220]"
              >
                Industries <span className="italic text-[#0F172A]/60">we serve.</span>
              </motion.h2>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-0 border-t border-[#0B1220]/15">
            {INDUSTRIES.map((ind, i) => (
              <motion.div
                key={ind}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                className={`group p-6 border-b border-[#0B1220]/15 ${
                  (i + 1) % 5 !== 0 ? "lg:border-r border-[#0B1220]/15" : ""
                } ${(i + 1) % 3 !== 0 ? "sm:border-r border-[#0B1220]/15" : ""} ${
                  (i + 1) % 2 !== 0 ? "border-r border-[#0B1220]/15" : ""
                } hover:bg-[#0B1220] hover:text-white transition-colors duration-500 cursor-default`}
              >
                <div className="font-mono text-[10px] text-gray-400 group-hover:text-[#C9A24E] transition-colors">
                  0{i + 1}
                </div>
                <div className="font-serif text-lg sm:text-xl mt-6 leading-tight">
                  {ind}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section
        id="services"
        className="relative py-28 sm:py-36 bg-[#F5F5F0] border-y border-gray-200"
        data-testid="brochure-services"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase"
          >
            05 — Services
          </motion.div>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="font-serif text-4xl sm:text-6xl mt-6 leading-[1.05] tracking-tight text-[#0B1220] max-w-4xl"
          >
            Proven expertise · Global reach · <span className="italic text-[#0F172A]/60">Continuous innovation.</span>
          </motion.h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-[#0B1220]/15">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.article
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.8, delay: (i % 3) * 0.08 }}
                  whileHover={{ y: -4 }}
                  className={`group relative p-8 border-b border-[#0B1220]/15 ${
                    (i + 1) % 3 !== 0 ? "lg:border-r border-[#0B1220]/15" : ""
                  } ${i % 2 === 0 ? "md:border-r lg:border-r border-[#0B1220]/15" : ""} bg-white hover:bg-[#0B1220] hover:text-white transition-colors duration-500 cursor-default`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs tracking-[0.28em] text-gray-400 group-hover:text-[#C9A24E] transition-colors">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon
                      size={26}
                      strokeWidth={1.2}
                      className="text-[#0B1220] group-hover:text-[#C9A24E] transition-colors"
                    />
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl mt-14 leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-gray-600 group-hover:text-white/70 transition-colors">
                    {s.body}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SOLUTIONS ============ */}
      <section
        id="solutions"
        className="relative py-28 sm:py-36 bg-white"
        data-testid="brochure-solutions"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase"
          >
            06 — Solutions
          </motion.div>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="font-serif text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-[#0B1220] max-w-3xl"
          >
            Our SAP solutions <span className="italic text-[#0F172A]/60">&amp; offerings.</span>
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={2}
            className="mt-6 text-lg text-gray-600 max-w-xl leading-relaxed"
          >
            Empowering business with tailored SAP solutions and expert services.
          </motion.p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SOLUTIONS.map((sol, i) => (
              <motion.div
                key={sol}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                whileHover={{ y: -3 }}
                className="group border border-[#0B1220]/15 p-5 flex items-center justify-between bg-white hover:border-[#C9A24E] hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-4">
                  <span className="font-mono text-[10px] tracking-[0.28em] text-gray-400 group-hover:text-[#C9A24E] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg text-[#0B1220]">{sol}</span>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-gray-300 group-hover:text-[#C9A24E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section
        id="contact"
        className="relative bg-[#0B1220] text-white py-28 sm:py-36 overflow-hidden"
        data-testid="brochure-contact"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#C9A24E]/10 blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="font-mono text-[11px] tracking-[0.32em] text-[#C9A24E] uppercase"
          >
            07 — Contact
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-6 items-end">
            <div className="md:col-span-7">
              <motion.h2
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="font-serif text-5xl sm:text-7xl leading-[1.02] tracking-tight"
              >
                Ready to grow <br />
                <span className="italic text-[#C9A24E]">your business?</span>
              </motion.h2>
            </div>
            <div className="md:col-span-5 space-y-5 text-[15px] text-white/80">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-[#C9A24E] shrink-0" />
                <span className="leading-relaxed">
                  301 &amp; 302, Surya Pearl Building, Opp: Raheja Mindspace IT Park,
                  Hitech City, Hyderabad – 500081
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-[#C9A24E] shrink-0" />
                <a href="mailto:info@sunsrisoft.com" className="hover:text-white">
                  info@sunsrisoft.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#C9A24E] shrink-0" />
                <a href="tel:+918790006313" className="hover:text-white">
                  +91 87900 06313
                </a>
              </div>
            </div>
          </div>

          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={handleDownload}
              data-testid="brochure-download-contact"
              className="btn-gold"
            >
              <Download size={16} /> Save this brochure as Word
            </button>
            <Link
              to="/contact"
              data-testid="brochure-contact-cta"
              className="inline-flex items-center gap-2 text-white border-b border-white/40 pb-1 hover:border-[#C9A24E] hover:text-[#C9A24E] transition-colors"
            >
              Start a conversation <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs uppercase tracking-[0.28em] text-white/40">
            <span>Sunsri Software Private Limited</span>
            <a
              href="https://www.sunsris.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C9A24E] hover:text-white transition"
            >
              www.sunsris.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
